<template>
  <transition name="ph-modal-fade">
    <div
      v-if="open"
      ref="backdrop"
      class="ph-modal-backdrop"
      @click.self="onClose"
      @keydown.esc.stop="onClose"
    >
      <div
        class="ph-modal phd-special-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="phd-special-title"
        tabindex="-1"
        ref="modal"
      >
        <button
          type="button"
          class="ph-modal-close"
          @click="onClose"
          aria-label="Close"
        >×</button>

        <header class="ph-modal-header">
          <span class="ph-modal-eyebrow">Per-course pricing</span>
          <h2 id="phd-special-title">{{ course && course.name ? course.name : 'How many users for this course?' }}</h2>
          <p>This course is priced per learner — separate from your team plan. Tell us how many people on your team will take it so we get the price exactly right.</p>
        </header>

        <form class="ph-modal-form" @submit.prevent="onConfirm">
          <label class="ph-field ph-field--full">
            <span class="ph-field-label">Number of users for this course *</span>
            <input
              type="number"
              min="1"
              :max="ceiling"
              v-model.number="users"
              class="ph-field-input phd-special-users"
              ref="usersInput"
              required
            />
            <small class="ph-promo-message">Defaults to your team size — tweak it if only a subset will take this course.</small>
          </label>

          <button
            type="submit"
            class="ph-btn ph-btn--checkout ph-modal-cta"
            :disabled="!isValid"
          >Add to plan →</button>
        </form>
      </div>
    </div>
  </transition>
</template>

<script>
import teleportToBody from '../../mixins/teleportToBody';

export default {
  name: 'SpecialCoursePromptModal',
  mixins: [teleportToBody],

  props: {
    open: { type: Boolean, default: false },
    course: { type: Object, default: null },
    // Default user count to seed the input (typically the team size).
    defaultUsers: { type: Number, default: 1 },
    // Hard cap so a typo can't ship an absurd 999999.
    ceiling: { type: Number, default: 100000 },
  },

  data() {
    return {
      users: this.defaultUsers || 1,
    };
  },

  computed: {
    isValid() {
      return Number.isFinite(Number(this.users))
        && Number(this.users) >= 1
        && Number(this.users) <= this.ceiling;
    },
  },

  watch: {
    open(val) {
      if (val) {
        // Reset to the team-default each time the modal opens — buyer's
        // last entry on a different course shouldn't carry over.
        this.users = this.defaultUsers || 1;
        this.$nextTick(() => {
          if (this.$refs.usersInput && typeof this.$refs.usersInput.focus === 'function') {
            this.$refs.usersInput.focus();
            this.$refs.usersInput.select();
          }
        });
      }
    },
    defaultUsers(val) {
      // If the team-size slider changes while the modal is closed, the
      // next open should reflect the new default too.
      if (!this.open) this.users = val || 1;
    },
  },

  methods: {
    onClose() {
      this.$emit('close');
    },
    onConfirm() {
      if (!this.isValid) return;
      this.$emit('confirm', {
        course: this.course,
        users: Number(this.users),
      });
    },
  },
};
</script>

<!-- Modal infrastructure: imported globally in main.js. -->
<style lang="scss" scoped>
.phd-special-modal {
  max-width: 460px;
}

.phd-special-users {
  font-size: 18px;
  font-weight: 700;
  text-align: center;
  letter-spacing: 0.02em;
  /* Hide the spinner — the up/down chrome is fiddly on touch and the
     buyer can just type. */
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}
</style>
